import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DashboardHeader from './components/DashboardHeader';
import FiltersBar from './components/FiltersBar';
import MetricsCards from './components/MetricsCards';
import ManagementReportExport from './components/ManagementReportExport';
import ChartsSection from './components/ChartsSection';
import ItemsChartsSection from './components/ItemsChartsSection';
import OrdersChartsSection from './components/OrdersChartsSection';
import ActionButton from '../ActionButton';
import { apiFetch } from '../../../utils/api';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { makeReportFilters, TAB_REQUESTS } from './reportConfig';
import './reports.css';
import { ReportExportContext, describeReportScope } from './components/ReportExportContext';

const reportsTheme = createTheme({ typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' } });
const EMPTY_DATA = {
  ordersByDay: [], avgBumpTimeByDay: [], ordersHeatmap: [], itemsData: [], itemsByDay: [],
  totalOrders: 0, ordersByTable: [], ordersByServer: [], ordersSummary: {}, ordersList: [],
  ordersItemsCount: [], ordersBumpMinutes: [],
};
const KDSAnalytics = () => {
  const { isDark, setDarkMode } = useDarkMode('reportsDarkMode');
  const initialFilters = useMemo(makeReportFilters, []);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [activeTab, setActiveTab] = useState('Overview');
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  const [loadedScope, setLoadedScope] = useState('');
  const [exportHierarchy, setExportHierarchy] = useState([]);
  const [error, setError] = useState('');
  const [canLoadLarge, setCanLoadLarge] = useState(false);
  const [dateRange, setDateRange] = useState({ earliest: '', latest: '' });
  const requestId = useRef(0);
  const controllerRef = useRef(null);

  const buildQueryString = useCallback((values, allowLargeReport = false) => {
    const params = new URLSearchParams();
    Object.entries(values || {}).forEach(([key, value]) => { if (value) params.append(key, value); });
    if (allowLargeReport) params.set('allowLargeReport', '1');
    return params.toString();
  }, []);

  const readJson = useCallback(async (path, signal) => {
    const response = await apiFetch(path, { signal });
    const raw = await response.text();
    let parsed = null;
    try { parsed = raw ? JSON.parse(raw) : null; } catch { /* handled below */ }
    if (!response.ok) {
      const failure = new Error(parsed?.error || `${path} failed with HTTP ${response.status}.`);
      failure.canLoadLarge = parsed?.can_retry_large_report === true;
      throw failure;
    }
    if (raw && parsed == null) throw new Error(`${path} returned invalid JSON.`);
    return parsed;
  }, []);

  const fetchReports = useCallback(async (tab, activeFilters, { allowLargeReport = false } = {}) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError('');
    setCanLoadLarge(false);
    const query = buildQueryString(activeFilters, allowLargeReport);
    const requests = TAB_REQUESTS[tab] || TAB_REQUESTS.Overview;
    setData(previous => {
      const next = { ...previous };
      requests.forEach(([key]) => { next[key] = key === 'ordersSummary' ? {} : []; });
      if (requests.some(([key]) => key === 'ordersByDay')) next.totalOrders = 0;
      return next;
    });
    const settled = await Promise.allSettled(requests.map(async ([key, route, optional]) => {
      try {
        return { key, value: await readJson(`/updates-reports/${route}?${query}`, controller.signal) };
      } catch (requestError) {
        if (optional) return { key, value: [] };
        throw requestError;
      }
    }));
    if (controller.signal.aborted || currentRequest !== requestId.current) return;
    const failures = settled.filter(result => result.status === 'rejected').map(result => result.reason);
    const successful = Object.fromEntries(settled.filter(result => result.status === 'fulfilled').map(result => [result.value.key, result.value.value]));
    setData(previous => {
      const next = { ...previous, ...successful };
      if (successful.ordersByDay) next.totalOrders = successful.ordersByDay.reduce((sum, day) => sum + Number(day.total_orders || 0), 0);
      return next;
    });
    if (failures.length) {
      setCanLoadLarge(failures.some(failure => failure.canLoadLarge));
      setError(failures.map(failure => failure.message).filter((message, index, all) => all.indexOf(message) === index).join(' '));
    }
    setLoading(false);
    setLoadedScope(JSON.stringify([tab, activeFilters]));
  }, [buildQueryString, readJson]);

  useEffect(() => {
    const controller = new AbortController();
    readJson('/updates-reports/report-date-range', controller.signal)
      .then(result => setDateRange({ earliest: String(result?.earliest_date || '').slice(0, 10), latest: String(result?.latest_date || '').slice(0, 10) }))
      .catch(() => setDateRange({ earliest: '', latest: '' }));
    return () => controller.abort();
  }, [readJson]);

  useEffect(() => {
    fetchReports(activeTab, appliedFilters);
    return () => controllerRef.current?.abort();
  }, [activeTab, appliedFilters, fetchReports]);

  const refresh = () => {
    const next = makeReportFilters();
    setFilters(next);
    if (JSON.stringify(next) === JSON.stringify(appliedFilters)) fetchReports(activeTab, next);
    else setAppliedFilters(next);
  };
  const fetchItemsSummaryForRange = useCallback(async (startDate, endDate) => {
    const query = buildQueryString({ ...appliedFilters, startDate, endDate });
    const rows = await readJson(`/updates-reports/items-summary?${query}`);
    return Array.isArray(rows) ? rows : [];
  }, [appliedFilters, buildQueryString, readJson]);

  return <ThemeProvider theme={reportsTheme}>
    <div className="reports-suite min-h-screen" data-theme={isDark ? 'dark' : 'light'}>
      <ReportExportContext.Provider value={{ filters: appliedFilters, summary: describeReportScope(appliedFilters, exportHierarchy), disabled: loading || Boolean(error) || loadedScope !== JSON.stringify([activeTab, appliedFilters]) }}>
      <DashboardHeader onRefresh={refresh} isDark={isDark} onToggleDarkMode={() => setDarkMode(!isDark)} />
      <FiltersBar filters={filters} onChange={setFilters} onApply={next => setAppliedFilters({ ...next })}
        onHierarchyChange={setExportHierarchy}
        earliestDate={dateRange.earliest} latestDate={dateRange.latest} resetValues={initialFilters} />
      <div className="reports-body">
        <div className="reports-tabs-row">
          <MetricsCards activeTab={activeTab} setActiveTab={setActiveTab} />
          {!loading && !error && loadedScope === JSON.stringify([activeTab, appliedFilters]) &&
            <ManagementReportExport filters={appliedFilters} isDark={isDark} />}
          {loading && <div role="status" className="reports-tab-loading"><CircularProgress size={20} />Loading {activeTab.toLowerCase()} report…</div>}
        </div>
        {error && <Alert severity="error" action={canLoadLarge ? <ActionButton onClick={() => fetchReports(activeTab, appliedFilters, { allowLargeReport: true })}>Load large report</ActionButton> : <ActionButton onClick={() => fetchReports(activeTab, appliedFilters)}>Retry</ActionButton>}>{error} Successful report sections remain available.</Alert>}
        {activeTab === 'Orders' && <OrdersChartsSection isDark={isDark} filters={appliedFilters} ordersByDay={data.ordersByDay} ordersHeatmap={data.ordersHeatmap} ordersByTable={data.ordersByTable} ordersByServer={data.ordersByServer} ordersSummary={data.ordersSummary} ordersList={data.ordersList} ordersItemsCount={data.ordersItemsCount} ordersBumpMinutes={data.ordersBumpMinutes} />}
        {activeTab === 'Items' && <ItemsChartsSection data={data.itemsData} totalOrders={data.totalOrders} isDark={isDark} filters={appliedFilters} />}
        {activeTab === 'Overview' && <ChartsSection filters={appliedFilters} isDark={isDark} ordersByDay={data.ordersByDay} avgBumpTimeByDay={data.avgBumpTimeByDay} ordersHeatmap={data.ordersHeatmap} itemsData={data.itemsData} itemsByDay={data.itemsByDay} fetchItemsSummaryForRange={fetchItemsSummaryForRange} ordersList={data.ordersList} ordersBumpMinutes={data.ordersBumpMinutes} />}
      </div>
      </ReportExportContext.Provider>
    </div>
  </ThemeProvider>;
};

export default KDSAnalytics;
